import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { PageHead, EmptyState } from "@/components/Bits";
import { StudioWizard } from "@/components/StudioWizard";
import { useStudioList } from "@/lib/useStudioList";
import { useConfig } from "@/lib/useConfig";
import { TierScore } from "@/components/Progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SUB_TINT = {
  active: "bg-emerald-50 text-emerald-700",
  trial: "bg-amber-50 text-amber-700",
  inactive: "bg-rose-50 text-rose-700",
};

export default function Studios() {
  const { rows, reload } = useStudioList();
  const regionOptions = useConfig("regions");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", city: "", region: "", address: "", phone: "", email: "" });
  const [details, setDetails] = useState([]);
  const [edit, setEdit] = useState(null);
  const [eform, setEform] = useState({});
  const [del, setDel] = useState(null);

  const loadDetails = () => api.get("/studios").then(({ data }) => setDetails(data));

  useEffect(() => {
    loadDetails();
  }, []);

  const submit = async () => {
    try {
      await api.post("/studios", form);
      toast.success("Studio created");
      setOpen(false);
      setForm({ name: "", city: "", region: "", address: "", phone: "", email: "" });
      reload();
      loadDetails();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openEdit = (s) => {
    const full = details.find((d) => d.id === s.studio_id) || {};
    setEform({
      name: full.name || s.name || "",
      city: full.city || s.city || "",
      region: full.region || "",
      address: full.address || "",
      phone: full.phone || "",
      email: full.email || "",
      gst_percent: full.gst_percent ?? 18,
      subscription_status: full.subscription_status || s.subscription_status || "active",
    });
    setEdit(s);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/studios/${edit.studio_id}`, {
        ...eform,
        gst_percent: Number(eform.gst_percent || 0),
      });
      toast.success(`${eform.name} updated`);
      setEdit(null);
      reload();
      loadDetails();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/studios/${del.studio_id}`);
      toast.success(`${del.name} deleted`);
      setDel(null);
      reload();
      loadDetails();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHead title="Studios" subtitle="Veloskin network">
        <StudioWizard
          regions={regionOptions}
          onDone={() => {
            reload();
            loadDetails();
          }}
        />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-studio-btn">
              <Plus className="mr-2 h-4 w-4" /> New studio
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>New studio</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {[
                ["name", "Name *"],
                ["city", "City *"],
                ["region", `Region (${regionOptions.map((r) => r.name).join(", ")})`],
                ["address", "Address"],
                ["phone", "Phone"],
                ["email", "Email (receives warranty certificate copies)"],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label>{label}</Label>
                  <Input
                    data-testid={`studio-${k}-input`}
                    value={form[k] ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button data-testid="save-studio-btn" onClick={submit} disabled={!form.name || !form.city}>
                Create studio
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHead>

      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="studios-table">
            <TableHeader>
              <TableRow>
                <TableHead>Studio</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-right">Subscription</TableHead>
                <TableHead className="text-right">Customers</TableHead>
                <TableHead className="text-right">Revenue (mo)</TableHead>
                <TableHead className="text-right">Profit (mo)</TableHead>
                <TableHead className="text-right">Jobs (mo)</TableHead>
                <TableHead className="text-right">Target</TableHead>
                <TableHead className="text-right">Tier</TableHead>
                <TableHead className="text-right">Reminders</TableHead>
                <TableHead className="text-right">Stock alert</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} className="p-0">
                    <EmptyState
                      icon={Plus}
                      testid="studios-empty-state"
                      title="No studios yet"
                      hint="Use the onboarding wizard to get a studio live with its team, opening stock and monthly targets in one flow."
                      action={
                        <StudioWizard
                          regions={regionOptions}
                          onDone={() => {
                            reload();
                            loadDetails();
                          }}
                        />
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
              {rows.map((s) => (
                <TableRow key={s.studio_id} className="hover:bg-brand-tint/60">
                  <TableCell className="font-medium">
                    <Link
                      to={`/studios/${s.studio_id}`}
                      data-testid={`studio-link-${s.studio_id}`}
                      className="hover:text-crimson-600"
                    >
                      {s.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-slate-500">{s.city}</TableCell>
                  <TableCell className="text-right">
                    <Badge
                      data-testid={`studio-sub-${s.studio_id}`}
                      className={`border-0 capitalize ${SUB_TINT[s.subscription_status] || "bg-slate-100 text-slate-700"}`}
                    >
                      {s.subscription_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{s.customers}</TableCell>
                  <TableCell className="text-right">{money(s.month_revenue)}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {money(s.month_profit)}
                  </TableCell>
                  <TableCell className="text-right">{s.month_jobs}</TableCell>
                  <TableCell className="text-right" data-testid={`studios-target-${s.studio_id}`}>
                    {s.revenue_progress_pct}%
                  </TableCell>
                  <TableCell className="text-right">
                    <TierScore score={s.tier_readiness} testid={`studios-tier-${s.studio_id}`} />
                  </TableCell>
                  <TableCell className="text-right">{s.pending_reminders}</TableCell>
                  <TableCell className="text-right">
                    {s.low_stock > 0 && (
                      <Badge
                        data-testid={`studio-low-stock-${s.studio_id}`}
                        className="border-0 bg-amber-50 text-amber-700"
                      >
                        <AlertTriangle className="mr-1 h-3 w-3" /> {s.low_stock}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[44px]"
                        data-testid={`edit-studio-${s.studio_id}`}
                        onClick={() => openEdit(s)}
                      >
                        <Pencil className="mr-1.5 h-4 w-4" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[44px] border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                        data-testid={`delete-studio-${s.studio_id}`}
                        onClick={() => setDel(s)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="mt-6">
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-studio-dialog">
          <DialogHeader>
            <DialogTitle>Edit {edit?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {[
              ["name", "Name *"],
              ["city", "City *"],
              ["region", `Region (${regionOptions.map((r) => r.name).join(", ")})`],
              ["address", "Address"],
              ["phone", "Phone"],
              ["email", "Email (receives warranty certificate copies)"],
              ["gst_percent", "GST %"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input
                  className="min-h-[44px]"
                  data-testid={`edit-studio-${k}-input`}
                  value={eform[k] ?? ""}
                  onChange={(e) => setEform((f) => ({ ...f, [k]: e.target.value }))}
                />
              </div>
            ))}
            <div>
              <Label>Subscription</Label>
              <div className="mt-1.5 flex gap-2">
                {["active", "trial", "inactive"].map((k) => (
                  <button
                    key={k}
                    data-testid={`edit-studio-sub-${k}`}
                    onClick={() => setEform((f) => ({ ...f, subscription_status: k }))}
                    className={`min-h-[44px] rounded px-4 text-sm font-medium capitalize transition-colors duration-200 ${
                      eform.subscription_status === k
                        ? "bg-brand text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setEdit(null)}>
              Cancel
            </Button>
            <Button
              className="min-h-[44px]"
              data-testid="save-edit-studio-btn"
              onClick={saveEdit}
              disabled={!eform.name || !eform.city}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-studio-dialog">
          <DialogHeader>
            <DialogTitle>Delete {del?.name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            A studio can only be deleted once it has no staff, customers or jobs left — otherwise
            set its subscription to <span className="font-medium">inactive</span> from Edit
            instead, which keeps all of its history.
          </p>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setDel(null)}>
              Cancel
            </Button>
            <Button
              className="min-h-[44px] bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-delete-studio-btn"
              onClick={confirmDelete}
            >
              Delete studio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
